/** Helpers for building venue risk assessment template definitions in the admin UI. */

const QUESTION_TYPES = [
  { value: 'select', label: 'Multiple choice' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'text', label: 'Text (not scored)' },
  { value: 'number', label: 'Number (not scored)' },
]

function slugId(prefix, existingIds = []) {
  const base = `${prefix}_${Date.now().toString(36)}`
  let id = base
  let n = 0
  while (existingIds.includes(id)) {
    n += 1
    id = `${base}_${n}`
  }
  return id
}

export function allQuestionIds(definition) {
  const ids = []
  for (const dim of definition?.dimensions || []) {
    for (const q of dim.questions || []) {
      if (q.id) ids.push(q.id)
    }
  }
  return ids
}

export function allDimensionIds(definition) {
  return (definition?.dimensions || []).map((d) => d.id).filter(Boolean)
}

export function newDimension(definition, label = 'New section') {
  const ids = allDimensionIds(definition)
  const sortOrder = (definition?.dimensions?.length || 0) + 1
  return {
    id: slugId('section', ids),
    label,
    weight: 0,
    sort_order: sortOrder,
    questions: [],
  }
}

export function newQuestion(dim, type = 'select') {
  const existing = (dim.questions || []).map((q) => q.id)
  const sortOrder = (dim.questions?.length || 0) + 1
  const base = {
    id: slugId('q', existing),
    label: 'New question',
    type,
    sort_order: sortOrder,
    required: type === 'select' || type === 'boolean',
    weight: 1,
  }
  if (type === 'select') {
    return {
      ...base,
      options: [
        { value: 'opt_low', label: 'Low risk', points: 1 },
        { value: 'opt_med', label: 'Medium risk', points: 2 },
        { value: 'opt_high', label: 'High risk', points: 4 },
      ],
    }
  }
  if (type === 'boolean') {
    return { ...base, points_if_true: 2, points_if_false: 0 }
  }
  if (type === 'text' || type === 'number') {
    return { ...base, scored: false, required: false }
  }
  return base
}

export function newSelectOption(question, label = 'New option') {
  const existing = (question.options || []).map((o) => o.value)
  const value = slugId('opt', existing)
  const maxPts = Math.max(...(question.options || []).map((o) => Number(o.points) || 0), 0)
  return { value, label, points: maxPts > 0 ? maxPts : 1 }
}

export function rebalanceDimensionWeights(dimensions, changedId, newWeight) {
  const dims = dimensions.map((d) => ({ ...d }))
  const idx = dims.findIndex((d) => d.id === changedId)
  if (idx < 0) return dims
  const w = Math.max(0, Math.min(100, Number(newWeight) || 0))
  dims[idx].weight = w
  const others = dims.filter((_, i) => i !== idx)
  const rest = 100 - w
  if (others.length === 0) {
    dims[idx].weight = 100
    return dims
  }
  const otherSum = others.reduce((s, d) => s + Number(d.weight) || 0, 0)
  if (otherSum <= 0) {
    const each = Math.floor(rest / others.length)
    others.forEach((d, i) => {
      const di = dims.findIndex((x) => x.id === d.id)
      dims[di].weight = i === others.length - 1 ? rest - each * (others.length - 1) : each
    })
  } else {
    others.forEach((d) => {
      const di = dims.findIndex((x) => x.id === d.id)
      dims[di].weight = Math.round((Number(d.weight) / otherSum) * rest)
    })
    const fix = 100 - dims.reduce((s, d) => s + Number(d.weight) || 0, 0)
    if (fix !== 0) {
      const lastOther = dims.findIndex((x) => x.id === others[others.length - 1].id)
      dims[lastOther].weight = Number(dims[lastOther].weight) + fix
    }
  }
  return dims
}

export { QUESTION_TYPES }
